import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/contexts/LanguageContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { Link } from "react-router-dom";
import { Barbell, Timer, CalendarBlank, ArrowRight } from "@phosphor-icons/react";
import { getFocusMeta } from "@/utils/focusMeta";
import { HighlightBadge } from "@/components/HighlightBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.4 } } };

export default function TrainingPage() {
  const [plans, setPlans] = useState([]);
  const [activeFocus, setActiveFocus] = useState("all");
  const { t } = useTranslation();
  const { formatPrice } = useCurrency();

  useEffect(() => { axios.get(`${API}/training-plans`).then(r => setPlans(r.data)).catch(() => {}); }, []);

  const levelColors = { principiante: "text-green-400 border-green-400/30", intermedio: "text-yellow-400 border-yellow-400/30", avanzado: "text-red-400 border-red-400/30" };

  // Únicos focus presentes en los planes (preserva el primer orden de aparición)
  const focusKeys = Array.from(new Set(plans.map(p => String(p.focus || "").toLowerCase().trim()).filter(Boolean)));
  const filteredPlans = activeFocus === "all" ? plans : plans.filter(p => String(p.focus || "").toLowerCase().trim() === activeFocus);

  return (
    <main className="pt-20 min-h-screen">
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-gray-400 mb-2">Pro Vertical</p>
          <h1 className="text-4xl sm:text-5xl tracking-tight leading-none font-black uppercase font-['Cabinet_Grotesk'] mb-4" data-testid="training-page-title">{t("training_title")}</h1>
          <p className="text-base text-gray-400 max-w-lg">{t("training_desc")}</p>
        </div>
      </section>
      <section className="pb-24" data-testid="training-list">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {focusKeys.length > 1 && (
            <div className="flex flex-wrap gap-2 mb-8" data-testid="training-focus-filters">
              <button
                onClick={() => setActiveFocus("all")}
                className={`px-4 h-9 text-xs uppercase tracking-wider font-bold border transition-colors inline-flex items-center gap-1.5 ${activeFocus === "all" ? "bg-[#FF4D00] border-[#FF4D00] text-white" : "bg-transparent border-white/15 text-gray-300 hover:border-white/40 hover:text-white"}`}
                data-testid="focus-chip-all"
              >Todos <span className="opacity-60">({plans.length})</span></button>
              {focusKeys.map(fk => {
                const { Icon: FI, label: FL } = getFocusMeta(fk);
                const count = plans.filter(p => String(p.focus || "").toLowerCase().trim() === fk).length;
                const active = activeFocus === fk;
                return (
                  <button
                    key={fk}
                    onClick={() => setActiveFocus(fk)}
                    className={`px-4 h-9 text-xs uppercase tracking-wider font-bold border transition-colors inline-flex items-center gap-1.5 ${active ? "bg-[#FF4D00] border-[#FF4D00] text-white" : "bg-transparent border-white/15 text-gray-300 hover:border-white/40 hover:text-white"}`}
                    data-testid={`focus-chip-${fk}`}
                  ><FI size={14} weight="bold" />{FL} <span className="opacity-60">({count})</span></button>
                );
              })}
            </div>
          )}
          <motion.div key={activeFocus} initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.1 } } }} className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {filteredPlans.map(plan => {
              const { Icon: FocusIcon, label: focusLabel } = getFocusMeta(plan.focus);
              return (
              <motion.div key={plan.id} variants={fadeUp} className="bg-[#121212] border border-white/5 overflow-hidden card-hover flex flex-col" data-testid={`training-card-${plan.id}`}>
                <Link to={`/entrenamientos/${plan.id}`} className="h-48 overflow-hidden block relative">
                  <img src={plan.image_url} alt={plan.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  <HighlightBadge text={plan.badge_text} color={plan.badge_color} />
                </Link>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-center gap-2 mb-3">
                    <Badge variant="outline" className={`text-xs ${levelColors[plan.level] || ""}`}>{plan.level}</Badge>
                    <Badge variant="outline" className="text-xs text-gray-400 border-white/10 inline-flex items-center gap-1" data-testid={`training-focus-${plan.id}`}><FocusIcon size={12} weight="bold" />{focusLabel}</Badge>
                  </div>
                  <h3 className="text-lg font-semibold mb-2 font-['Cabinet_Grotesk']">{plan.title}</h3>
                  <p className="text-sm text-gray-400 leading-relaxed mb-4 flex-1 line-clamp-3 whitespace-pre-wrap">{plan.description}</p>
                  <div className="flex items-center gap-4 text-xs text-gray-400 mb-4">
                    <span className="flex items-center gap-1"><CalendarBlank size={14} />{plan.duration_weeks} {t("weeks")}</span>
                    <span className="flex items-center gap-1"><Timer size={14} />{plan.sessions_per_week}{t("per_week")}</span>
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-white/5">
                    <span className="text-xl font-bold text-[#FF4D00]">{plan.price === 0 ? t("free_label") : formatPrice(plan.price)}</span>
                    <Link to={`/entrenamientos/${plan.id}`}>
                      <Button size="sm" className="bg-[#FF4D00] hover:bg-[#E64500] text-white font-bold" data-testid={`training-view-${plan.id}`}>{t("details")} <ArrowRight size={12} className="ml-1" /></Button>
                    </Link>
                  </div>
                </div>
              </motion.div>
              );
            })}
          </motion.div>
          {plans.length === 0 && <div className="text-center py-20 text-gray-400"><Barbell size={48} className="mx-auto mb-4 opacity-30" /><p>{t("no_plans")}</p></div>}
          {plans.length > 0 && filteredPlans.length === 0 && <div className="text-center py-20 text-gray-400"><Barbell size={48} className="mx-auto mb-4 opacity-30" /><p>Sin planes para este enfoque.</p></div>}
        </div>
      </section>
    </main>
  );
}
